import api from './api';

export interface User {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  environment: string | null;
}

interface AuthResponse {
  user: Omit<User, 'isAdmin' | 'environment'>;
  isAdmin?: boolean;
  environment?: string | null;
}

function toUser(response: AuthResponse): User {
  return {
    ...response.user,
    isAdmin: response.isAdmin ?? false,
    environment: response.environment ?? null
  };
}

export async function login(email: string, password: string): Promise<User> {
  const { data } = await api.post<AuthResponse>('auth/login', { email, password });
  return toUser(data);
}

export async function register(name: string, email: string, password: string): Promise<User> {
  const { data } = await api.post<AuthResponse>('auth/register', { name, email, password });
  return toUser(data);
}

export async function googleLogin(idToken: string): Promise<User> {
  const { data } = await api.post<AuthResponse>('auth/google', { idToken });
  return toUser(data);
}

let currentUserRequest: Promise<User | null> | null = null;

export function getCurrentUser(): Promise<User | null> {
  if (!currentUserRequest) {
    currentUserRequest = api
      .get<AuthResponse>('auth/me')
      .then(({ data }) => toUser(data))
      .catch(() => null)
      .finally(() => {
        currentUserRequest = null;
      });
  }
  return currentUserRequest;
}

export async function initiateRegistration(name: string, email: string, password: string): Promise<void> {
  await api.post('auth/register/initiate', { name, email, password });
}

export async function verifyRegistration(email: string, code: string): Promise<User> {
  const { data } = await api.post<AuthResponse>('auth/register/verify', { email, code });
  return toUser(data);
}

export async function resendVerificationCode(email: string): Promise<void> {
  await api.post('auth/register/resend', { email });
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('auth/forgot-password', { email });
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  await api.post('auth/reset-password', { email, code, newPassword });
}

export async function logout(): Promise<void> {
  await api.post('auth/logout');
}
